import { Component, type ErrorInfo, type ReactNode } from 'react';
import { IonPage, IonContent, IonButton, IonIcon } from '@ionic/react';
import { alertCircle, refresh } from 'ionicons/icons';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Lazy-loaded chunks can 404 right after a deploy when a user's tab was
 * open before the new bundle rolled out — the old index.html references
 * chunk hashes that no longer exist. We want to auto-reload once in that
 * case instead of forcing the user through the "Reîncearcă" button.
 */
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message ?? '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

const RELOAD_STORAGE_KEY = 'err-boundary-reloaded-at';
const RELOAD_WINDOW_MS = 30_000; // avoid reload loops

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    if (isChunkLoadError(error)) {
      // Only auto-reload if we haven't already in the last 30s — otherwise
      // a genuinely broken chunk would loop the page forever.
      const last = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY) ?? 0);
      if (Date.now() - last > RELOAD_WINDOW_MS) {
        sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <IonPage>
          <IonContent className="ion-padding">
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', height: '100%', textAlign: 'center', padding: 40,
            }}>
              <IonIcon icon={alertCircle} style={{ fontSize: 64, color: '#dc2626', marginBottom: 16 }} />
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Ceva nu a mers bine</h2>
              <p style={{ fontSize: 14, color: '#64748b', margin: '0 0 24px', maxWidth: 400 }}>
                A aparut o eroare neasteptata. Incearca sa reincarci pagina.
              </p>
              <IonButton onClick={this.handleReset}>
                <IonIcon icon={refresh} slot="start" />
                Reincearca
              </IonButton>
            </div>
          </IonContent>
        </IonPage>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
